import { Stack, styled, Switch, SwitchProps } from '@mui/material';
import { useToggle } from '@vestwell-frontend/hooks';

import { FC, ReactNode, useCallback } from 'react';

import { Text } from './Text';

export type SwitchToggleProps = {
  'aria-label'?: string;
  checked?: boolean;
  disabled?: boolean;
  labels?: {
    off?: ReactNode;
    on?: ReactNode;
  };
  onChange?(checked: boolean): void;
};

const StyledSwitch = styled(Switch, {
  shouldForwardProp: propName => propName !== 'isFocused'
})<SwitchProps & { isFocused: boolean }>(props => ({
  borderRadius: props.theme.spacing(3),
  height: props.theme.spacing(6),
  padding: 0,
  width: props.theme.spacing(10),
  ...(props.isFocused
    ? {
        outline: `1px solid ${props.theme.palette.primary.main}`,
        outlineOffset: '2px'
      }
    : {}),
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      '& + .MuiSwitch-track': {
        backgroundColor: props.theme.palette.primary.main,
        opacity: 1
      },
      '& .MuiSwitch-thumb': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 16 16" width="16"><path d="M0 0h24v24H0z" fill="none"/><path fill="${encodeURIComponent(
          props.theme.palette.primary.main
        )}" d="M5.99998 10.7799L3.21998 7.9999L2.27332 8.9399L5.99998 12.6666L14 4.66656L13.06 3.72656L5.99998 10.7799Z"/></svg>')`
      },
      color: props.theme.palette.white.main,
      transform: 'translateX(16px)'
    },
    '&.Mui-disabled': {
      '& + .MuiSwitch-track': {
        backgroundColor: props.theme.palette.grey500.main,
        color: props.theme.palette.grey500.main,
        opacity: 1
      },
      '& .MuiSwitch-thumb': {
        backgroundColor: props.theme.palette.grey600.main,
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 16 16" width="16"><path fill="${encodeURIComponent(
          props.theme.palette.grey300.main
        )}" d="M12.6666 8.66683H3.33331V7.3335H12.6666V8.66683Z"/></svg>')`,
        color: props.theme.palette.grey600.main
      },
      '&.Mui-checked': {
        '& .MuiSwitch-thumb': {
          backgroundColor: props.theme.palette.grey600.main,
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 16 16" width="16"><path d="M0 0h24v24H0z" fill="none"/><path fill="${encodeURIComponent(
            props.theme.palette.grey400.main
          )}" d="M5.99998 10.7799L3.21998 7.9999L2.27332 8.9399L5.99998 12.6666L14 4.66656L13.06 3.72656L5.99998 10.7799Z"/></svg>')`,
          color: props.theme.palette.grey600.main
        }
      }
    },
    margin: 2,
    padding: 0,
    transitionDuration: '300ms'
  },
  '& .MuiSwitch-thumb': {
    backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 16 16" width="16"><path fill="${encodeURIComponent(
      props.theme.palette.grey300.main
    )}" d="M12.6666 8.66683H3.33331V7.3335H12.6666V8.66683Z"/></svg>')`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    height: props.theme.spacing(5),
    width: props.theme.spacing(5)
  },
  '& .MuiSwitch-track': {
    backgroundColor: props.theme.palette.grey400.main,
    borderRadius: props.theme.spacing(3),
    opacity: 1,
    transition: props.theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}));

export const SwitchToggle: FC<SwitchToggleProps> = ({
  checked = false,
  labels = {
    off: 'Off',
    on: 'On'
  },
  ...props
}) => {
  const [isChecked, toggleChecked] = useToggle(checked);

  const [isFocused, toggleIsFocused] = useToggle(false);

  const onChange = useCallback(() => {
    if (props.onChange) {
      props.onChange(!isChecked);
    }

    toggleChecked();
  }, [isChecked, props.onChange]);

  return (
    <Stack alignItems='center' direction='row' spacing={1}>
      {labels?.off && (
        <Text color={isChecked ? 'grey300' : 'grey50'} m={0} variant='i1'>
          {labels.off}
        </Text>
      )}
      <StyledSwitch
        checked={isChecked}
        data-component='switchToggle'
        disabled={props.disabled}
        inputProps={{
          'aria-checked': isChecked,
          'aria-label': props['aria-label'],
          role: 'switch'
        }}
        isFocused={isFocused}
        onBlur={toggleIsFocused}
        onChange={onChange}
        onFocus={toggleIsFocused}
      />
      {labels?.on && (
        <Text color={isChecked ? 'grey300' : 'grey50'} m={0} variant='i1'>
          {labels.on}
        </Text>
      )}
    </Stack>
  );
};

SwitchToggle.displayName = 'SwitchToggle';
